import React, { forwardRef, useMemo, useState } from "react";
import { keyframes, styled } from "goober";

import { getLocaleString } from "@/util/i18n-helper.mjs";
import SuppressHydrationWarnings from "@/util/SuppressHydrationWarnings.jsx";
import useResizeObserver from "@/util/use-resize-observer.mjs";

const xAxisDefault = {
  visible: false,
  ticks: 0,
  tickRenderer: () => {},
};

const yAxisDefault = {
  visible: true,
  ticks: 7,
  tickRenderer: () => {},
};
const marginDefault = {
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
};
const genTicks = (conf, size, offset, toScreen, toValue) => {
  if (!conf) return [];
  if (!conf.visible) return [];

  if (Array.isArray(conf.ticks)) {
    if (typeof conf.ticks[0] === "number") {
      return conf.ticks.map((tick) => ({
        label: tick,
        pos: toScreen(tick),
      }));
    }
    return conf.ticks.map((tick, idx) => {
      return {
        label: tick,
        pos: (offset + (idx * size) / (conf.ticks.length - 1)).toFixed(0),
      };
    });
  }

  const tickCount = conf.ticks - 1 || 1;
  const tickWidth = size || 1;
  const ticks = [];
  for (let i = 0; i <= tickCount; i++) {
    const tickPos = offset + i * (tickWidth / tickCount);
    ticks.push({
      label: conf.tickRenderer
        ? conf.tickRenderer(toValue(tickPos))
        : toValue(tickPos),
      pos: tickPos.toFixed(0),
    });
  }
  return ticks;
};

const MultiLineChart = (props) => {
  const {
    data,
    data2 = [],
    yField,
    width,
    height,
    color,
    color2,
    fill,
    fill2,
    circleRadius = 6,
    showGridLines = false,
    showOneGridLine = false,
    gridLinesColor = "var(--shade6)",
    margin = marginDefault,
    xAxisConf = xAxisDefault,
    yAxisConf = yAxisDefault,
    showTooltip = false,
    valueOptions,
    hideCircles = false,
    yAxisPosition = "left",
    onHover,
    showHoverLine = false,
    hoverBoth = false,
    pathFilter = "none",
  } = props;
  const [graphContainerRef, boundingClientRect] = useResizeObserver();
  const [hoverPoint, setHoverPoint] = useState(null);
  const [hoverX, setHoverX] = useState(null);
  const hoverIndex =
    parseInt(hoverPoint?.replace("graph1", "").replace("graph2", "")) || 0;

  // get container width and height
  const { graphWidth, graphHeight } = useMemo(() => {
    return {
      graphWidth: width || boundingClientRect?.width || 0,
      graphHeight: height || boundingClientRect?.height || 0,
    };
  }, [width, height, boundingClientRect]);

  // calc min and max of data.
  const { yMin, yMax } = useMemo(() => {
    let yValues = [];
    if (Array.isArray(yAxisConf.ticks)) {
      yValues = yAxisConf.ticks;
    } else {
      yValues = data
        ?.map((v) => v[yField])
        .concat(data2?.map((v) => v[yField])) || [0];
    }

    return {
      yMin: yValues?.length ? Math.min(...yValues) : 0,
      yMax: yValues?.length ? Math.max(...yValues) : 0,
    };
  }, [yAxisConf.ticks, data, data2, yField]);

  const xStrength = data?.length - 1 || 1;
  const { left = 0, right = 0 } = margin;
  const xScale = (graphWidth - left - right) / xStrength;

  // calc graph points
  const [graphPoints, yAxisTicks, xAxisTicks, graphPoints2] = useMemo(() => {
    const xStrength = data?.length - 1 || 1;
    const yStrength = yMax - yMin || 1;
    const { left = 0, right = 0, bottom = 0, top = 0 } = margin;
    const xScale = (graphWidth - left - right) / xStrength;
    const yScale = (graphHeight - top - bottom) / yStrength;

    const y2Screen = (yValue) =>
      graphHeight - bottom - (yValue - yMin) * yScale || 0;
    const y2Value = (yPos) => (graphHeight - bottom - yPos) / yScale + yMin;
    const x2Screen = (xValue) => left + xValue * xScale || 0;
    const x2Value = (xPos) => (xPos - left) / xScale;

    const yTicks = genTicks(
      yAxisConf,
      graphHeight - top - bottom,
      top,
      y2Screen,
      y2Value,
    );
    const xTicks = genTicks(
      xAxisConf,
      graphWidth - left - right,
      left,
      x2Screen,
      x2Value,
    );
    const points1 = data?.map((v, idx) => {
      return {
        ...v,
        x: left + idx * xScale,
        y: y2Screen(v[yField]),
        label: v.label || v.x,
        value: v.y,
      };
    });
    const points2 = data2?.map((v, idx) => {
      return {
        ...v,
        x: left + idx * xScale,
        y: y2Screen(v[yField]),
        label: v.label || v.x,
        value: v.y,
      };
    });

    return [points1, yTicks, xTicks, points2];
  }, [
    data,
    yMax,
    yMin,
    margin,
    graphWidth,
    graphHeight,
    yAxisConf,
    xAxisConf,
    data2,
    yField,
  ]);

  // get svg path
  const svgPath = graphPoints?.reduce(
    (acc, v, idx) => (idx === 0 ? `M${v.x},${v.y}` : `${acc}L${v.x},${v.y}`),
    "",
  );
  const svgPath2 = graphPoints2?.reduce(
    (acc, v, idx) => (idx === 0 ? `M${v.x},${v.y}` : `${acc}L${v.x},${v.y}`),
    "",
  );
  const hoveringPath = graphPoints
    ?.slice(0, hoverIndex + 1)
    .reduce(
      (acc, v, idx) => (idx === 0 ? `M${v.x},${v.y}` : `${acc}L${v.x},${v.y}`),
      "",
    );
  const hoveringPath2 = graphPoints
    ?.slice(hoverIndex, graphPoints.length)
    .reduce(
      (acc, v, idx) => (idx === 0 ? `M${v.x},${v.y}` : `${acc}L${v.x},${v.y}`),
      "",
    );

  const svgDimensions = {
    width: width ? graphWidth : undefined,
    height: height ? graphHeight : undefined,
  };

  const tooltipKey = data.reduce((acc, a) => acc + a[yField], 0);

  return (
    <SuppressHydrationWarnings>
      <GraphContainer ref={graphContainerRef}>
        <LinesContainer>
          <svg {...svgDimensions} className="main-graph">
            <g className="y-axis">
              {yAxisConf?.visible &&
                yAxisTicks.map((v, i) => (
                  <g key={"y-axis-" + v.label + i} className="tick">
                    <text
                      x={
                        (yAxisPosition === "left"
                          ? margin?.left
                          : graphWidth) || 0
                      }
                      y={v.pos}
                      dy={3}
                    >
                      {v.label}
                    </text>
                  </g>
                ))}
            </g>

            <g className="x-axis">
              {xAxisConf?.visible && (
                <g>
                  {xAxisConf?.dots &&
                    graphPoints.map((p) => (
                      <circle
                        cx={p.x}
                        cy={graphHeight - 15}
                        r={1}
                        key={"x-dots" + p.x + p}
                        style={{ fill: "var(--shade3)" }}
                      />
                    ))}
                  {xAxisTicks.map((v, i) => (
                    <g key={"x-axis" + v.label + i} className="tick">
                      {typeof v.label === "string" ||
                      typeof v.label === "number" ? (
                        <text x={v.pos} y={graphHeight} dx={3}>
                          {v.label}
                        </text>
                      ) : (
                        <svg x={v.pos} y={graphHeight} dx={3}>
                          {v.label}
                        </svg>
                      )}
                    </g>
                  ))}
                </g>
              )}
            </g>

            <g
              className="grid-lines"
              fill="none"
              style={{
                stroke: gridLinesColor,
              }}
            >
              {showGridLines &&
                yAxisTicks.map((v, i) => {
                  if (
                    showOneGridLine &&
                    i !== Math.floor(yAxisTicks.length / 2)
                  ) {
                    return null;
                  }
                  const svgHorizLinePath = `M${margin?.left || 0},${v.pos} L${
                    graphWidth - margin?.right
                  },${v.pos}`;
                  return <path key={"gridlines-" + i} d={svgHorizLinePath} />;
                })}
            </g>

            <g className="graph-lines" style={{ stroke: color2 }}>
              {graphPoints2?.length > 0 && (
                <path
                  d={svgPath2}
                  stroke={color2 ? color2 : color}
                  fill={fill2}
                  style={{ strokeWidth: "2px" }}
                />
              )}
            </g>

            {graphPoints?.length > 0 ? (
              <g className="graph-lines">
                {hoverPoint && hoverBoth ? (
                  <>
                    <path
                      d={hoveringPath}
                      style={{
                        stroke: color,
                        strokeWidth: "3px",
                        filter: pathFilter,
                      }}
                    />
                    <path
                      d={hoveringPath2}
                      style={{ stroke: `var(--shade3)`, strokeWidth: "3px" }}
                    />
                  </>
                ) : (
                  <path
                    d={svgPath}
                    style={{
                      stroke: color,
                      strokeWidth: "3px",
                      filter: pathFilter,
                    }}
                    fill={fill}
                  />
                )}
              </g>
            ) : (
              ""
            )}

            <g className="circle-points" style={{ fill: color2 }}>
              {showHoverLine && hoverPoint && hoverX ? (
                <line
                  x1={hoverX}
                  y1={0}
                  x2={hoverX}
                  y2={graphHeight - 10}
                  style={{ stroke: "var(--shade4)" }}
                />
              ) : (
                ""
              )}

              {graphPoints2?.map((p, idx) => {
                const spread = {
                  cx: p.x,
                  cy: p.y,
                  r: circleRadius,
                };
                if (typeof data2[idx].color === "string") {
                  spread.style = {
                    fill: data2[idx].color,
                  };
                }
                return (
                  <g
                    key={"points-" + p.y + idx}
                    onMouseEnter={() => {
                      setHoverPoint("graph2" + idx);
                      setHoverX(p.x);
                      if (onHover)
                        onHover({
                          y1: graphPoints?.[idx]?.value,
                          y2: graphPoints2?.[idx]?.value,
                        });
                    }}
                    onMouseLeave={() => {
                      setHoverPoint(null);
                      if (onHover) onHover(null);
                    }}
                  >
                    <circle
                      style={{
                        stroke:
                          hoverPoint === "graph2" + idx ? color2 : undefined,
                        opacity:
                          hideCircles &&
                          !(hoverPoint === "graph2" + idx) &&
                          !(hoverBoth && hoverPoint === "graph1" + idx)
                            ? 0
                            : 1,
                      }}
                      {...spread}
                    />
                    <TooltipBox
                      showTooltip={showTooltip}
                      hoverPoint={hoverPoint}
                      graph={"graph2"}
                      idx={idx}
                      p={p}
                      tooltipKey={tooltipKey}
                      valueOptions={valueOptions}
                    />
                  </g>
                );
              })}
            </g>

            <g className="circle-points" style={{ fill: color }}>
              {graphPoints?.map((p, idx) => {
                const spread = {
                  cx: p.x,
                  cy: p.y,
                  r: circleRadius,
                };
                if (typeof data[idx].color === "string") {
                  spread.style = {
                    fill: data[idx].color,
                  };
                }
                return (
                  <g
                    key={"points-" + p.y + idx}
                    onMouseEnter={() => {
                      setHoverPoint("graph1" + idx);
                      setHoverX(p.x);
                      if (onHover)
                        onHover({
                          y1: graphPoints?.[idx]?.value,
                          y2: graphPoints2?.[idx]?.value,
                        });
                    }}
                    onMouseLeave={() => {
                      setHoverPoint(null);
                      if (onHover) onHover(null);
                    }}
                  >
                    <g
                      style={{
                        opacity:
                          hideCircles &&
                          !(hoverPoint === "graph1" + idx) &&
                          !(hoverBoth && hoverPoint === "graph2" + idx)
                            ? 0
                            : 1,
                      }}
                    >
                      <circle
                        style={{
                          stroke:
                            hoverPoint === "graph1" + idx ? color : undefined,
                        }}
                        {...spread}
                      />
                      {xScale ? (
                        <rect
                          x={p.x - xScale / 2}
                          y={0}
                          width={Math.max(xScale, 0)}
                          height={`100%`}
                          fill={`transparent`}
                          style={{ opacity: 0 }}
                        />
                      ) : (
                        ""
                      )}
                    </g>
                    <TooltipBox
                      showTooltip={showTooltip}
                      hoverPoint={hoverPoint}
                      graph={"graph1"}
                      idx={idx}
                      p={p}
                      tooltipKey={tooltipKey}
                      valueOptions={valueOptions}
                    />
                  </g>
                );
              })}
            </g>

            <use xlinkHref={`#tooltip${tooltipKey}`} />
          </svg>
        </LinesContainer>
      </GraphContainer>
    </SuppressHydrationWarnings>
  );
};

export default MultiLineChart;

const TooltipBox = ({
  showTooltip,
  hoverPoint,
  idx,
  p,
  graph,
  tooltipKey,
  valueOptions,
}) => {
  return (
    showTooltip &&
    hoverPoint === graph + idx && (
      <g
        id={"tooltip" + tooltipKey}
        className="tooltip-container"
        transform={`translate(${p.x - 50}, ${p.y - 55})`}
      >
        <rect></rect>
        <text>
          <tspan x="0" dy="0" className="label">
            {p.label}
          </tspan>
          <tspan x="0" dy="1.5em" className="value">
            {getLocaleString(p.value, valueOptions)}
          </tspan>
        </text>
      </g>
    )
  );
};

const GraphContainer = styled("div", forwardRef)`
  display: flex;
  height: 100%;
  position: relative;
`;

const animStroke = () => keyframes`
  from {
    stroke-width: 0;
  }
  to {
    stroke-width: var(--sp-1_5);
  }
`;

// It is assumed that *all* graphs the server generates will differ slightly from
// the client due to cached data.
const LinesContainer = styled("div")`
  position: relative;
  display: flex;
  height: 100%;

  .main-graph {
    overflow: visible;
    pointer-events: all;
    max-width: 100%;
  }

  .circle-points {
    stroke-width: 2;
    stroke: #1e1e20;

    circle:hover {
      stroke-opacity: 0.3;
      stroke-width: 6;
      r: 4;
      animation: ${animStroke} 0.1s ease-in;
    }
  }

  .graph-lines {
    stroke-width: 2;
    fill: none;
  }

  .grid-lines {
    stroke-width: var(--sp-px);
    stroke-dasharray: 2, 4;
  }

  .tick {
    letter-spacing: 2.3px;
    fill: var(--shade3);
    font-size: var(--sp-4);
  }

  .y-axis {
    text-anchor: end;
    transform: translateX(calc(-1 * var(--sp-3)));
  }

  .x-axis {
    text-anchor: middle;
    transform: translateY(calc(-1 * var(--sp-5)));
  }

  .tooltip-container {
    transform-origin: center center;

    text {
      fill: var(--shade0);
      stroke: none;
      font-weight: 400;
      font-size: var(--sp-3_5);
      text-anchor: middle;
      transform: translate(var(--sp-12_5), 0);

      .label {
        font-weight: 500;
        font-size: var(--sp-2_5);
        line-height: var(--sp-2_5);
        letter-spacing: 1.5px;
        text-transform: uppercase;
        fill: var(--shade3);
      }

      .value {
        font-weight: 400;
        font-size: var(--sp-3_5);
        line-height: 21px;
      }
    }
    rect {
      width: var(--sp-29);
      height: var(--sp-15);
      fill: var(--shade10);
      x: calc(-1 * var(--sp-2));
      y: calc(-1 * var(--sp-5_5));
      rx: var(--br);
      ry: var(--br);
      stroke-width: 0;
      filter: drop-shadow(0 var(--sp-1) var(--sp-5) var(--shade10-15));
    }
  }
`;
